/**
 * StudiesBreadcrumb — the trail on the study and negotiation pages
 * (WP-103, decision (290), 2026-09-29): Studies > protocol > negotiation.
 * Every crumb but the last is a link when it carries `to`; the last is plain
 * text marked aria-current="page". A crumb's text is its label alone (no icon
 * inside the link), so a test can read a crumb's textContent as the label.
 */
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

export interface Crumb {
  label: string;
  to?: string;
}

export function StudiesBreadcrumb({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" data-testid="studies-breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-sm">
        {items.map((c, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${i}-${c.label}`} className="flex min-w-0 items-center gap-1">
              {i > 0 && (
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              )}
              {!isLast && c.to ? (
                <Link
                  to={c.to}
                  data-testid={`studies-crumb-${i}`}
                  title={c.label}
                  className="inline-flex min-h-[44px] max-w-[16rem] items-center truncate rounded font-medium text-gray-600 transition-colors duration-150 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:text-primary-400 sm:min-h-0"
                >
                  {c.label}
                </Link>
              ) : (
                <span
                  data-testid={`studies-crumb-${i}`}
                  aria-current={isLast ? 'page' : undefined}
                  title={c.label}
                  className="max-w-[20rem] truncate font-medium text-gray-900 dark:text-gray-100"
                >
                  {c.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default StudiesBreadcrumb;
